/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  SparklesIcon,
  UnstyledButton,
  XRetweetIcon,
} from '@buffer-mono/popcorn'

import type { DraftAsset } from '~publish/components/Composer/assets/assetListOps'
import type {
  MediaRun,
  MediaSlide,
} from '~publish/components/Composer/assets/mediaRuns'
import {
  gifAsImage,
  toMediaRuns,
} from '~publish/components/Composer/assets/mediaRuns'
import type {
  Gif,
  Image,
  Link,
  Retweet,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'
import { AttachmentTypes } from '~publish/legacy/constants'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import type { LinkType } from '~publish/legacy/shared-components/LinkifiedText/LinkifiedText'
import { parseTwitterLinks } from '~publish/legacy/utils/channels/twitter/helpers'

import { useExpandablePreviewText } from '../../../Common/hooks/useExpandablePreviewText'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import { ImagesGrid } from '../../../Common/ImagesGrid'
import { LinkPreview } from '../../../Common/LinkPreview'
import { PreviewAiMarker } from '../../../Common/styles'
import { DEFAULT_AVATAR } from '../../../Common/utils'
import type { TwitterContent, TwitterProfile } from '../../../types'
import { AttachmentRetweet } from '../AttachmentRetweet'
import { AttachmentVideo } from '../AttachmentVideo'
import { TweetActions } from './TweetActions'

import styles from './Tweet.module.css'

interface GetMediaPropertiesResult {
  hasMedia: boolean
  hasImages: boolean | number | undefined | null | Gif
  hasVideo: boolean
  hasLink: boolean
  isQuoteTweet: boolean
  isRetweet: boolean
}

const getMediaProperties = (
  content: TwitterContent,
  linkPreview: Link | null,
): GetMediaPropertiesResult => {
  const { text, images, gif, video, enabledAttachmentType, retweet } = content

  const hasText = !!text
  const hasMedia = enabledAttachmentType === AttachmentTypes.MEDIA
  const hasImages = hasMedia && (images?.length || gif)
  const hasVideo = !!(hasMedia && video)
  const hasLink = !!(linkPreview && !hasImages && !hasVideo)
  const hasContent = hasText || hasMedia || hasLink
  // If there is no content, we display the whole tweet as a retweet.
  const isQuoteTweet =
    enabledAttachmentType === AttachmentTypes.RETWEET && hasContent
  const isRetweet =
    enabledAttachmentType === AttachmentTypes.RETWEET && !hasContent

  // For simple retweets (isRetweet = true), check if the original tweet has media
  const retweetHasImages = !!(
    isRetweet && retweet?.media?.some((m) => m.type === 'photo')
  )
  const retweetHasVideo = !!(
    isRetweet &&
    retweet?.media?.some((m) => m.type === 'video' || m.type === 'animated_gif')
  )

  return {
    hasMedia,
    hasImages: hasImages || retweetHasImages,
    hasVideo: hasVideo || retweetHasVideo,
    hasLink,
    isQuoteTweet,
    isRetweet,
  }
}

export const Tweet = ({
  content,
  profile,
  index,
  isAiGenerated,
}: {
  content: TwitterContent
  profile: TwitterProfile
  index: number
  isAiGenerated?: boolean
}): JSX.Element => {
  const {
    text,
    images,
    gif,
    retweet,
    video,
    videos,
    link: linkFromPost,
    inThread,
  } = content
  const { avatar, username, formattedUsername, displayName, isTwitterPremium } =
    profile
  const [linkPreview, linkText] = useLinkPreviewEffect({
    linkFromPost,
    text,
  })

  const { hasMedia, hasLink, isQuoteTweet, isRetweet } = getMediaProperties(
    content,
    linkPreview,
  )

  const showAiMarker = Boolean(isAiGenerated)

  const {
    avatarUrl: retweetAvatar,
    text: retweetText,
    userDisplayName: retweetDisplayName,
    userName: retweetUsername,
  } = retweet || {}

  const resolvedAvatar = isRetweet ? retweetAvatar : avatar
  const [avatarSrc, setAvatarSrc] = useState(resolvedAvatar || DEFAULT_AVATAR)

  useEffect(() => {
    setAvatarSrc(resolvedAvatar || DEFAULT_AVATAR)
  }, [resolvedAvatar])

  const originalText = isRetweet ? retweetText : text
  const isPremiumTruncated =
    isTwitterPremium && (originalText?.length ?? 0) > 280

  const { textToDisplay, isExpanded, expand } = useExpandablePreviewText({
    channel: 'twitter',
    text: originalText,
    linkToRemove: linkText,
    hasLink,
  })

  // For retweets, use the original tweet's media; otherwise use current post's media
  let mediaRuns: MediaRun[]

  if ((isRetweet || isQuoteTweet) && retweet?.media) {
    const retweetImages: Image[] = []
    let retweetVideo: Video | null = null
    retweet.media.forEach((mediaItem) => {
      if (mediaItem.type === 'photo') {
        retweetImages.push({
          url: mediaItem.url,
          width: mediaItem.width,
          height: mediaItem.height,
          altText: null,
          source: null,
          mediaType: 'image',
          originalUrl: null,
        })
      } else if (
        mediaItem.type === 'video' ||
        mediaItem.type === 'animated_gif'
      ) {
        retweetVideo = {
          uploadId: mediaItem.id,
          name: mediaItem.type === 'animated_gif' ? 'GIF' : 'Video',
          duration: mediaItem.duration_ms ? mediaItem.duration_ms / 1000 : 0,
          durationMs: mediaItem.duration_ms || 0,
          size: 0,
          width: mediaItem.width,
          height: mediaItem.height,
          url: mediaItem.url,
          originalUrl: mediaItem.url,
          thumbnail: mediaItem.url,
          availableThumbnails: [mediaItem.url],
          thumbOffset: 0,
          mediaType: 'video',
        }
      }
    })
    mediaRuns = [
      ...(retweetImages.length
        ? [{ kind: 'images' as const, images: retweetImages }]
        : []),
      ...(retweetVideo
        ? [{ kind: 'video' as const, video: retweetVideo }]
        : []),
    ]
  } else {
    // `content.assets` is the ordered image/video/gif source of truth when
    // the caller provides it. Callers that haven't adopted it yet fall back
    // to the legacy images-array-or-gif-fallback plus separate video field.
    const assets: readonly DraftAsset[] | null | undefined = content.assets
    const mediaSlides: MediaSlide[] = hasMedia
      ? assets?.length
        ? assets.reduce<MediaSlide[]>((acc, asset) => {
            if (asset.kind === 'image') {
              acc.push({ kind: 'image', image: asset.image })
            } else if (asset.kind === 'gif') {
              acc.push({ kind: 'image', image: gifAsImage(asset.gif) })
            } else if (asset.kind === 'video') {
              acc.push({ kind: 'video', video: asset.video })
            }
            return acc
          }, [])
        : [
            ...(images?.length
              ? images.map((image) => ({ kind: 'image' as const, image }))
              : gif
                ? [{ kind: 'image' as const, image: gifAsImage(gif) }]
                : []),
            ...(videos?.length
              ? videos.map((v) => ({ kind: 'video' as const, video: v }))
              : video
                ? [{ kind: 'video' as const, video }]
                : []),
          ]
      : []
    mediaRuns = toMediaRuns(mediaSlides)
  }

  const usernameToDisplay = displayName || username

  return (
    <>
      {isRetweet && (
        <p className={styles.retweetLabel} data-testid="retweet-label">
          <XRetweetIcon size="small" />
          <span translate="no">{usernameToDisplay}</span> reposted
        </p>
      )}
      <div
        className={clsx(styles.wrapper, inThread && styles.withBorder)}
        data-thread-id={index}
      >
        <img
          className={styles.avatar}
          alt={`${isRetweet ? retweetDisplayName : usernameToDisplay}'s avatar`}
          data-testid="avatar"
          src={avatarSrc}
          onError={(): void => {
            if (avatarSrc !== DEFAULT_AVATAR) setAvatarSrc(DEFAULT_AVATAR)
          }}
        />
        <div className={styles.usernameWrapper} translate="no">
          <span className={styles.username}>
            {isRetweet ? retweetDisplayName : usernameToDisplay}
          </span>
          <span className={styles.formattedUsername}>
            {isRetweet ? `@${retweetUsername}` : formattedUsername}
          </span>
        </div>
        <div className={styles.content}>
          <LinkifiedText
            links={parseTwitterLinks(textToDisplay) as LinkType[]}
            whitespace="pre-wrap"
          >
            {textToDisplay}
          </LinkifiedText>
          {isPremiumTruncated && !isExpanded && (
            <UnstyledButton onClick={expand} className={styles.showMoreButton}>
              Show more
            </UnstyledButton>
          )}
        </div>
        {mediaRuns.map((run) =>
          run.kind === 'images' ? (
            <ImagesGrid
              key={run.images.map((image) => image.url).join(',')}
              images={run.images}
              isRowFormat={true}
              maxHeight="180px"
              shouldShowGifTag={false}
              className={styles.imagesGrid}
            />
          ) : (
            <AttachmentVideo key={run.video.url} video={run.video} />
          ),
        )}
        {hasLink && !isQuoteTweet && (
          <LinkPreview
            link={linkPreview as Link}
            clickable={false}
            imageCover
            className={styles.linkPreview}
          />
        )}
        {isQuoteTweet && <AttachmentRetweet data={retweet as Retweet} />}
        {showAiMarker && (
          <PreviewAiMarker
            icon={<SparklesIcon />}
            label="Made with AI"
            variant="inline"
            className={styles.aiMarkerFooter}
          />
        )}
        <TweetActions />
      </div>
    </>
  )
}
